import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "./api";

const Ctx = createContext(null);

/**
 * Global project-filter state — multi-select with server-side persistence.
 *
 * Selection is stored on the user document (`selected_project_ids`) and
 * mirrored to localStorage for instant boot. Any change PATCHes the server
 * so the choice survives across devices.
 */
export function ProjectFilterProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [selectedIds, setSelectedIdsState] = useState(() => {
    try {
      const raw = localStorage.getItem("ens_selected_project_ids");
      if (raw) return JSON.parse(raw);
      const userRaw = localStorage.getItem("ens_user");
      if (userRaw) {
        const u = JSON.parse(userRaw);
        if (Array.isArray(u.selected_project_ids)) return u.selected_project_ids;
      }
      const legacy = localStorage.getItem("ens_selected_project");
      return legacy ? [legacy] : [];
    } catch { return []; }
  });

  // Debounce server persistence so rapid toggles don't spam the API
  const persistTimer = useRef(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/active-projects");
      // 149-co (PROMPT 16-P0-R1): filter WO dari project list. WO harus dipilih
      // via Monitoring Working Order, bukan lewat Project Filter global.
      const filtered = data.filter((p) =>
        (p.status === "active" || p.status === "awaiting_payment") &&
        p.project_type !== "working_order"
      );
      setProjects(filtered);
      // Auto-clean saved preferensi: buang selected id yang tidak ada di list
      // valid (misal WO ID dari preferensi lama).
      const validIds = new Set(filtered.map((p) => p.id));
      setSelectedIdsState((cur) => {
        const clean = cur.filter((id) => validIds.has(id));
        if (clean.length !== cur.length) {
          if (clean.length > 0) localStorage.setItem("ens_selected_project_ids", JSON.stringify(clean));
          else localStorage.removeItem("ens_selected_project_ids");
        }
        return clean;
      });
    } catch (e) { console.warn("Failed to reload active projects", e); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Sync with server-side preference on mount (in case another device changed it)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (Array.isArray(data.selected_project_ids)) {
          setSelectedIdsState(data.selected_project_ids);
          localStorage.setItem("ens_selected_project_ids", JSON.stringify(data.selected_project_ids));
        }
      } catch { /* silent */ }
    })();
  }, []);

  const persist = (ids) => {
    if (ids.length > 0) localStorage.setItem("ens_selected_project_ids", JSON.stringify(ids));
    else localStorage.removeItem("ens_selected_project_ids");
    localStorage.removeItem("ens_selected_project"); // clean legacy key
    // Debounced server sync (300ms)
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      api.patch("/auth/preferences", { selected_project_ids: ids }).catch(() => {});
    }, 300);
  };

  const setSelectedIds = useCallback((ids) => {
    setSelectedIdsState(ids);
    persist(ids);
  }, []);

  const selectAllActive = useCallback(() => {
    const ids = projects.map((p) => p.id);
    setSelectedIdsState(ids);
    persist(ids);
  }, [projects]);

  const toggleId = useCallback((id) => {
    setSelectedIdsState((cur) => {
      const has = cur.includes(id);
      const next = has ? cur.filter((x) => x !== id) : [...cur, id];
      persist(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => { setSelectedIdsState([]); persist([]); }, []);

  const setSelectedId = useCallback((id) => {
    const next = id ? [id] : [];
    setSelectedIdsState(next);
    persist(next);
  }, []);

  const selectedId = selectedIds.length > 0 ? selectedIds[0] : null;
  const selected = useMemo(() => projects.find((p) => p.id === selectedId) || null, [projects, selectedId]);
  const selectedProjects = useMemo(() => projects.filter((p) => selectedIds.includes(p.id)), [projects, selectedIds]);
  const headOffice = useMemo(() => projects.find((p) => p.internal_key === "head_office") || null, [projects]);

  const value = useMemo(() => ({
    projects,
    selectedIds, selectedProjects,
    selectedId, selected,
    headOffice,
    isAll: selectedIds.length === 0,
    toggleId, clearAll, setSelectedIds, selectAllActive,
    setSelectedId,
    reload,
  }), [projects, selectedIds, selectedProjects, selectedId, selected, headOffice, toggleId, clearAll, setSelectedIds, selectAllActive, setSelectedId, reload]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useProjectFilter = () => useContext(Ctx);
